import React from 'react';
import type { FormikValues } from 'formik';
import { Formik, setIn } from 'formik';
import * as Yup from 'yup';

import { useStyles } from '~/assets/styles';
import CardDialog from '~/components/CardDialog';
import { getAllSearchableContactRoles } from '~/components/communications/ContactUtils';
import QueueAvailabilityIndicator from '~/components/communications/PhoneCall/components/QueueAvailabilityIndicator';
import type { CallableAdjuster, QueueDetails } from '~/components/communications/PhoneCall/types';
import ContactTextFieldFormik from '~/components/ContactTextFieldFormik';
import AlertBanner from '~/components/core/AlertBanner';
import ButtonsContainer from '~/components/core/Atomic/Buttons/ButtonsContainer';
import Grid from '~/components/core/Atomic/Grid/Grid';
import MenuItem from '~/components/core/Atomic/MenuItem';
import FsButton from '~/components/core/FsWrappers/FsButton/FsButton';
import Text from '~/components/core/TextComponents/Text';
import { useCms } from '~/components/hooks/useCms';
import LoadingIndicator from '~/components/LoadingIndicator';
import useOrganization from '~/components/OrganizationContext';
import RadioButtonFormik from '~/components/RadioButtonFormik';
import TextFieldFormik from '~/components/TextFieldFormik';
import useDataFetcher from '~/components/useDataFetcher';
import type { ForwardCallParams } from '~/server_shared/openapi/data-contracts';
import type { ContactFullModel } from '~/server_shared/swagger/data-contracts';
import { TWILIO_ADJUSTER_STATUS_DICT } from '~/Types';
import { yupPhoneValidation } from '~/Utils/phoneValidationUtils';

export interface ForwardCallFormValues extends ForwardCallParams {
  contact?: ContactFullModel;
}

interface ForwardCallDialogProps {
  participantsDetails?: number[];
  onSubmit: (values: ForwardCallFormValues) => Promise<void>;
  onCancel: () => void;
  isClaimCommunication?: boolean;
}

const isTwilioAdjusterStatus = (status: string): status is keyof typeof TWILIO_ADJUSTER_STATUS_DICT => {
  return status in TWILIO_ADJUSTER_STATUS_DICT;
};

const ForwardCallDialog: React.FC<ForwardCallDialogProps> = ({
  participantsDetails,
  onSubmit,
  onCancel,
  isClaimCommunication = false,
}) => {
  const { classes } = useStyles();
  const { user } = useCms();
  // @ts-expect-error organizationContactRolesDict is not typed
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  const { organizationContactRolesDict } = useOrganization();

  const {
    isLoading: isLoadingAdjusters,
    isError: isErrorAdjusters,
    data: callableAdjuster = [],
  } = useDataFetcher<CallableAdjuster[]>(
    `/api/v1/organizations/${user.organization_id}/callable_adjusters_with_status`
  );

  const {
    isLoading: isLoadingQueues,
    isError: isErrorQueues,
    data: queuesDetails = [],
  } = useDataFetcher<QueueDetails[]>(`/api/v1/organizations/${user.organization_id}/calling_twilio_queues_status`);

  return (
    <Formik<ForwardCallFormValues>
      initialValues={{
        forward_to_type: 'adjuster',
        adjuster_id: '',
        queue_name: '',
        phone_number: '',
        contact_id: null,
        contact_phone_id: null,
      }}
      enableReinitialize
      validationSchema={Yup.object().shape({
        forward_to_type: Yup.string().required('Required'),
        adjuster_id: Yup.string().when('forward_to_type', {
          is: 'adjuster',
          then: Yup.string().required('Required'),
        }),
        queue_name: Yup.string().when('forward_to_type', {
          is: 'queue',
          then: Yup.string().required('Required'),
        }),
        phone_number: Yup.string().when('forward_to_type', {
          is: 'phone_number',
          then: yupPhoneValidation.required('Required'),
        }),
        contact_id: Yup.number()
          .nullable()
          .when('forward_to_type', {
            is: 'contact',
            then: Yup.number().nullable().required('Required'),
          }),
        contact: Yup.object()
          .nullable()
          .when('forward_to_type', {
            is: 'contact',
            then: Yup.object().test('is-phone-exists', 'Contact must contain at least one phone number', (contact) => {
              const c = contact as unknown as ContactFullModel;
              return Boolean(c && c.phones && c.phones.length > 0);
            }),
          }),
        contact_phone_id: Yup.number()
          .nullable()
          .when('forward_to_type', {
            is: 'contact',
            then: Yup.number().nullable().required('Required'),
          }),
      })}
      onSubmit={async (values, formikProps) => {
        try {
          await onSubmit(values);
        } catch (error) {
          formikProps.setSubmitting(false);
        }
      }}
    >
      {({ values, handleSubmit, isSubmitting }) => (
        <CardDialog
          title="Forward Call"
          isDialog
          maxWidth="sm"
          fullWidth
          onClose={onCancel}
          preventClose={isSubmitting}
          height="400px"
        >
          <Grid container spacing={1}>
            {participantsDetails && participantsDetails.length > 0 ? (
              <Grid item xs={12} className="mb-8">
                <AlertBanner
                  note="All additional participants would be disconnected from the call"
                  alertType={AlertBanner.ALERT_TYPES.WARNING}
                  withIcon
                />
              </Grid>
            ) : null}
            <Grid item xs={12}>
              <Text
                variant={Text.VARIANTS.LG}
                weight={Text.WEIGHTS.SEMI_BOLD}
                className="inline-flex h-full items-center"
              >
                Forward to
              </Text>
            </Grid>
            <Grid item xs={12}>
              <div className="flex flex-col gap-2">
                <RadioButtonFormik id="forward_to_type" label="User" optionValue="adjuster" />
                <RadioButtonFormik id="forward_to_type" label="Queue" optionValue="queue" />
                <RadioButtonFormik id="forward_to_type" label="Phone Number" optionValue="phone_number" />
                <RadioButtonFormik id="forward_to_type" label="Contact" optionValue="contact" />
              </div>
            </Grid>
            {!values.forward_to_type ? (
              <Grid item xs={12}>
                <div style={{ visibility: 'hidden' }}>
                  <TextFieldFormik id="place_holder" label="place_holder" fullWidth />
                </div>
              </Grid>
            ) : null}
            {values.forward_to_type === 'adjuster' ? (
              isLoadingAdjusters ? (
                <LoadingIndicator isError={isErrorAdjusters} classes={classes} />
              ) : (
                <Grid item xs={7}>
                  <TextFieldFormik id="adjuster_id" label="User" select fullWidth>
                    {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
                    {/*@ts-ignore - TextField type issues with MenuItem children*/}
                    {callableAdjuster
                      .filter(({ adjuster_id }) => adjuster_id !== user.id)
                      .map(({ adjuster_id, adjuster_name, status }) => (
                        <MenuItem
                          key={adjuster_id}
                          value={adjuster_id}
                          disabled={
                            !isTwilioAdjusterStatus(status) ||
                            !TWILIO_ADJUSTER_STATUS_DICT[status]?.can_accept_direct_incoming_calls
                          }
                        >
                          <div className="flex items-center">
                            <Text variant={Text.VARIANTS.SM} weight={Text.WEIGHTS.REGULAR}>
                              {adjuster_name}
                            </Text>
                            <span className="ml-4" />
                            <Text variant={Text.VARIANTS.XS} weight={Text.WEIGHTS.REGULAR}>
                              ({isTwilioAdjusterStatus(status) && TWILIO_ADJUSTER_STATUS_DICT[status]?.desc})
                            </Text>
                          </div>
                        </MenuItem>
                      ))}
                  </TextFieldFormik>
                </Grid>
              )
            ) : null}
            {values.forward_to_type === 'queue' ? (
              isLoadingQueues ? (
                <LoadingIndicator isError={isErrorQueues} classes={classes} />
              ) : (
                <Grid item xs={7}>
                  <TextFieldFormik id="queue_name" label="Queue" select fullWidth>
                    {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
                    {/*@ts-ignore - TextField type issues with MenuItem children*/}
                    {queuesDetails.map(({ queue_sid, workers, name }) => (
                      <MenuItem key={queue_sid} value={name}>
                        <div className="flex flex-wrap items-center">
                          <QueueAvailabilityIndicator queuesWorker={workers} />
                          <span className="ml-4" />
                          <Text variant={Text.VARIANTS.SM} weight={Text.WEIGHTS.REGULAR}>
                            {name}
                          </Text>
                        </div>
                      </MenuItem>
                    ))}
                  </TextFieldFormik>
                </Grid>
              )
            ) : null}
            {values.forward_to_type === 'phone_number' ? (
              <Grid item xs={7}>
                <TextFieldFormik id="phone_number" label="Phone Number" fullWidth />
              </Grid>
            ) : null}
            {values.forward_to_type === 'contact' ? (
              <>
                <Grid item xs={7}>
                  <ContactTextFieldFormik
                    id="contact"
                    label="Contact"
                    outOfContactsContextSearch={!isClaimCommunication}
                    acceptedRoles={getAllSearchableContactRoles(organizationContactRolesDict)}
                    // Note: ContactTextFieldFormik already sets contact_id + contact_full_name
                    getValuesOnSelectContact={(contact: ContactFullModel | '', valuesWithContact: FormikValues) =>
                      setIn(
                        valuesWithContact,
                        'contact_phone_id',
                        !contact || !contact.id || !contact.phones?.length ? null : contact.phones[0].id
                      )
                    }
                    fullWidth
                  />
                </Grid>
                {values.contact && values?.contact?.phones && values.contact.phones.length > 0 && (
                  <Grid item xs={12}>
                    {values.contact.phones.length === 1 ? (
                      <Text variant={Text.VARIANTS.SM} weight={Text.WEIGHTS.MEDIUM}>
                        {values.contact.phones[0].phone}
                      </Text>
                    ) : (
                      <TextFieldFormik id="contact_phone_id" label="Phone" select fullWidth>
                        {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
                        {/*@ts-ignore - TextField type issues with MenuItem children*/}
                        {values.contact.phones.map((phone) => (
                          <MenuItem key={phone.id} value={phone.id}>
                            {phone.phone} ({phone.phone_type})
                          </MenuItem>
                        ))}
                      </TextFieldFormik>
                    )}
                  </Grid>
                )}
              </>
            ) : null}
          </Grid>
          <ButtonsContainer>
            <FsButton variant="contained" color="primary" onClick={handleSubmit} disabled={isSubmitting}>
              Forward Call
            </FsButton>
          </ButtonsContainer>
        </CardDialog>
      )}
    </Formik>
  );
};

export default ForwardCallDialog;
